import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {format} from "date-fns"
import {
  Area,
  CartesianGrid,
  ComposedChart,
  DefaultZIndexes,
  DotItemDotProps,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  useXAxisScale,
  useYAxisScale,
  XAxis,
  XAxisTickContentProps,
  YAxis,
  ZIndexLayer,
} from "recharts"

import {GenericErrorAlert} from "../../../components/Alert"
import {Loading} from "../../../components/Loading"
import {TReachMeta, TReachRange, useReachResultsOverTimeQuery} from "../../../queries/reachEngine"
import {getDateFnsLocale} from "../../../utils/dateArithmetics"
import {useResizeRef} from "../../../utils/useResizeRef"
import {TReachPeriod} from "../period"
import {
  axisTicks,
  projectResults,
  toTimeline,
  TResultsBucket,
  TResultsPoint,
  TResultsProjection,
  valueTicks,
  windowDaysFor,
} from "../resultsProjection"
import {DashboardCard} from "./DashboardCard"
import {EmptyState} from "./EmptyState"
import {
  CHART_AXIS_STROKE,
  CHART_AXIS_TICK,
  CHART_CURSOR_STROKE,
  CHART_GRID_STROKE,
  CHART_VALUE_TICK,
  formatAxisTick,
  LegendItem,
  MQL_COLOR,
  MQL_TEXT_COLOR,
  SQL_COLOR,
  SqlMqlLegend,
  SqlMqlTooltip,
} from "./ResultsChart"

export const formatDate = (date: number, pattern = "d MMM") => format(date, pattern, getDateFnsLocale())

const isEmptyResults = (buckets: readonly TResultsBucket[]) => {
  const last = buckets.at(-1)
  return !last || (last.cumulative_sql === 0 && last.cumulative_mql === 0)
}

// One row per plotted date: the actual weeks, then the projected points. Today's row carries both its actual
// values and the projection's starting values, so the dashed lines continue the solid ones.
type TChartRow = {
  date: number
  kind: "actual" | "today" | "projected"
  labelled: boolean
  bucket?: TResultsBucket
  cumulative_sql?: number
  cumulative_mql?: number
  projected_sql?: number
  projected_mql?: number
  // Today's SQL, held flat: the results if outreach stopped today.
  flat_sql?: number
  // The pipeline at risk, between the flat line and the projected SQL.
  at_risk?: [number, number]
}

const toRows = (timeline: readonly TResultsPoint[], projection: TResultsProjection | null, ticks: number[]) => {
  const today = timeline[timeline.length - 1]
  const actual = timeline.map<TChartRow>((point, index) => ({
    date: point.date,
    kind: index === timeline.length - 1 ? "today" : "actual",
    labelled: ticks.includes(point.date),
    bucket: point,
    cumulative_sql: point.cumulative_sql,
    cumulative_mql: point.cumulative_mql,
  }))
  if (!projection) {
    return actual
  }
  const flat = today.cumulative_sql
  actual[actual.length - 1] = {
    ...actual[actual.length - 1],
    projected_sql: flat,
    projected_mql: today.cumulative_mql,
    flat_sql: flat,
    at_risk: [flat, flat],
  }
  return [
    ...actual,
    ...projection.points.map<TChartRow>(point => ({
      date: point.date,
      kind: "projected",
      labelled: true,
      projected_sql: point.sql,
      projected_mql: point.mql,
      flat_sql: flat,
      at_risk: [flat, point.sql],
    })),
  ]
}

// Room above the plot and after the last date for the end-value pills and the last label.
const CHART_MARGIN = {top: 24, right: 32, left: 0, bottom: 0}
const Y_AXIS_WIDTH = 48
// A generous average glyph width at the axis's 10.5px font size, to tell whether two date labels overlap.
const AXIS_CHAR_WIDTH = 6.5

const dateLabelWidth = (date: number) => formatDate(date).length * AXIS_CHAR_WIDTH

const ANIMATION_DURATION = 600
const ANIMATION = {animationDuration: ANIMATION_DURATION, animationEasing: "ease-in-out"} as const
// The projection appears once the actual lines have been drawn.
const PROJECTION_ANIMATION = {...ANIMATION, animationBegin: ANIMATION_DURATION} as const

// Concrete colours: CSS variables don't resolve inside SVG paint attributes.
const PROJECTED_TICK_FILL = "rgb(195, 202, 224)" // --color-cr-slate-30
const FLAT_STROKE = "rgb(154, 162, 188)" // --color-cr-slate-40
const AT_RISK_FILL = "rgb(17, 221, 221)" // #11DDDD
const TODAY_STROKE = "rgb(195, 202, 224)" // --color-cr-slate-30

// End-value pills, as in the design: 11.5px/700 on white, their top 20px above the value's point.
const PILL_FONT_SIZE = 11.5
const PILL_HEIGHT = 17
const PILL_RISE = 20
const PILL_PADDING_X = 5
// A generous average glyph width at the pill's font size, to size its background.
const PILL_CHAR_WIDTH = 7
// The least vertical space between two pills on the same date.
const PILL_GAP = 2

type TPill = {key: string; date: number; value: number; color: string}

// Pills on the same date are nudged apart vertically: the lowest keeps its place above its point and each
// one above it moves up just enough to clear it. A stack that would rise above the chart is held at its top
// instead, and pushes the pills under it down.
const pillTops = (pills: readonly TPill[], yScale: (value: number) => number | undefined) => {
  const tops = new Map<string, number>()
  new Set(pills.map(pill => pill.date)).forEach(date => {
    const placed = pills
      .filter(pill => pill.date === date)
      .map(pill => ({key: pill.key, top: (yScale(pill.value) ?? 0) - PILL_RISE}))
      .sort((a, b) => b.top - a.top)
    placed.forEach((pill, index) => {
      const below = placed[index - 1]
      pill.top = below ? Math.min(pill.top, below.top - PILL_HEIGHT - PILL_GAP) : pill.top
    })
    let lowestFreeTop = 0
    placed.reverse().forEach(pill => {
      pill.top = Math.max(pill.top, lowestFreeTop)
      lowestFreeTop = pill.top + PILL_HEIGHT + PILL_GAP
      tops.set(pill.key, pill.top)
    })
  })
  return tops
}

// The pills are placed from the axis scales, not the rendered ticks: Recharts' rendered-tick hooks are left
// empty under StrictMode. They paint in the label layer, above the lines and dots.
const EndPills: React.FC<{pills: readonly TPill[]}> = ({pills}) => {
  const xScale = useXAxisScale()
  const yScale = useYAxisScale()
  if (!xScale || !yScale) {
    return null
  }
  const tops = pillTops(pills, value => yScale(value))

  return (
    <ZIndexLayer zIndex={DefaultZIndexes.label}>
      <g className={"results-pills"}>
        {pills.map(pill => {
          const x = xScale(pill.date)
          const top = tops.get(pill.key)
          if (x === undefined || top === undefined) {
            return null
          }
          const label = Math.round(pill.value).toLocaleString()
          const width = label.length * PILL_CHAR_WIDTH + 2 * PILL_PADDING_X
          return (
            <g key={pill.key} className={"results-pill"} data-pill={pill.key}>
              <rect x={x - width / 2} y={top} width={width} height={PILL_HEIGHT} rx={5} fill={"white"} />
              <text
                x={x}
                y={top + PILL_HEIGHT / 2}
                dy={"0.35em"}
                textAnchor={"middle"}
                fontSize={PILL_FONT_SIZE}
                fontWeight={700}
                fill={pill.color}
              >
                {label}
              </text>
            </g>
          )
        })}
      </g>
    </ZIndexLayer>
  )
}

// Dots mark only the labelled weeks and the projected points, as in the design.
const rowDot =
  (r: number, fill: string, marks: (row: TChartRow) => boolean) =>
  ({cx, cy, payload, index}: DotItemDotProps) =>
    marks(payload as TChartRow) && cx != null && cy != null ? (
      <circle key={index} className={"results-dot"} cx={cx} cy={cy} r={r} fill={fill} />
    ) : null

const isLabelledActual = (row: TChartRow) => row.labelled && row.kind !== "projected"
const isProjected = (row: TChartRow) => row.kind === "projected"

const SQL_DOT = rowDot(4, SQL_COLOR, isLabelledActual)
const MQL_DOT = rowDot(3.5, MQL_COLOR, isLabelledActual)
const PROJECTED_SQL_DOT = rowDot(4, SQL_COLOR, isProjected)
const PROJECTED_MQL_DOT = rowDot(3.5, MQL_COLOR, isProjected)

// A date on the time axis, as one text node; projected dates are paler.
const DateTick: React.FC<XAxisTickContentProps & {projectedDates: ReadonlySet<number>}> = ({
  x,
  y,
  payload,
  className,
  projectedDates,
}) => {
  const date = Number(payload.value)
  return (
    <text
      className={className}
      x={x}
      y={y}
      dy={"0.71em"}
      textAnchor={"middle"}
      fontSize={CHART_AXIS_TICK.fontSize}
      fill={projectedDates.has(date) ? PROJECTED_TICK_FILL : CHART_AXIS_TICK.fill}
    >
      {formatDate(date)}
    </text>
  )
}

type TChartProps = {timeline: readonly TResultsPoint[]; projection: TResultsProjection | null}

const ResultsOverTimeChart: React.FC<TChartProps> = ({timeline, projection}) => {
  const {t} = useTranslation()

  const [chartWidth, setChartWidth] = React.useState(0)
  const chartRef = useResizeRef(element => setChartWidth(element.getBoundingClientRect().width))
  const plotWidth = chartWidth - CHART_MARGIN.left - CHART_MARGIN.right - Y_AXIS_WIDTH
  const ticks = React.useMemo(
    () => axisTicks(timeline, projection, {plotWidth, labelWidth: dateLabelWidth}),
    [timeline, projection, plotWidth]
  )
  const rows = React.useMemo(() => toRows(timeline, projection, ticks), [timeline, projection, ticks])
  const today = timeline[timeline.length - 1]
  const end = projection?.points[1]
  const pills: TPill[] = [
    {key: "sql-today", date: today.date, value: today.cumulative_sql, color: SQL_COLOR},
    {key: "mql-today", date: today.date, value: today.cumulative_mql, color: MQL_TEXT_COLOR},
    ...(end
      ? [
          {key: "sql-end", date: end.date, value: end.sql, color: SQL_COLOR},
          {key: "mql-end", date: end.date, value: end.mql, color: MQL_TEXT_COLOR},
        ]
      : []),
  ]

  const renderTick = React.useCallback(
    (props: XAxisTickContentProps) => (
      <DateTick {...props} projectedDates={new Set(projection?.points.map(point => point.date))} />
    ),
    [projection]
  )

  const renderTooltip = React.useCallback(
    ({active, payload}: TooltipContentProps) => {
      if (!active || !payload?.length) {
        return null
      }
      const row = payload[0].payload as TChartRow
      const date = formatDate(row.date, "d MMM yyyy")
      if (row.kind === "projected" && projection) {
        const halfHorizon = projection.horizonDays / 2
        return (
          <SqlMqlTooltip
            projected
            heading={date}
            kind={t("ReachEngine_ResultsOverTime_TooltipProjected")}
            sql={Math.round(row.projected_sql ?? 0)}
            mql={Math.round(row.projected_mql ?? 0)}
            deltas={{
              sql: Math.round(projection.sqlPerDay * halfHorizon),
              mql: Math.round(projection.mqlPerDay * halfHorizon),
            }}
          />
        )
      }
      return (
        <SqlMqlTooltip
          heading={
            row.kind === "today"
              ? t("ReachEngine_ResultsOverTime_TooltipToday", {date})
              : t("ReachEngine_ResultsOverTime_TooltipWeekEnding", {date})
          }
          kind={t("ReachEngine_ResultsOverTime_TooltipActual")}
          sql={row.cumulative_sql ?? 0}
          mql={row.cumulative_mql ?? 0}
          deltas={{sql: row.bucket?.sql ?? 0, mql: row.bucket?.mql ?? 0}}
        />
      )
    },
    [t, projection]
  )

  return (
    <>
      <div ref={chartRef} className={"w-full"} style={{height: 240}} data-testid={"results-over-time-chart"}>
        <ResponsiveContainer width={"100%"} height={"100%"}>
          <ComposedChart data={rows} margin={CHART_MARGIN} aria-label={t("ReachEngine_ResultsOverTime_Title")}>
            <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              dataKey={"date"}
              type={"number"}
              domain={["dataMin", "dataMax"]}
              ticks={ticks}
              interval={0}
              tickLine={false}
              axisLine={{stroke: CHART_AXIS_STROKE}}
              tick={renderTick}
            />
            <YAxis
              allowDecimals={false}
              width={Y_AXIS_WIDTH}
              domain={projection ? [0, projection.yMax] : undefined}
              ticks={projection ? valueTicks(projection.yMax) : undefined}
              tickFormatter={formatAxisTick}
              tickLine={false}
              axisLine={false}
              tick={CHART_VALUE_TICK}
            />
            <Tooltip content={renderTooltip} cursor={{stroke: CHART_CURSOR_STROKE}} />
            <Area
              type={"monotone"}
              dataKey={"cumulative_sql"}
              className={"results-sql-area"}
              fill={SQL_COLOR}
              fillOpacity={0.07}
              stroke={"none"}
              activeDot={false}
              tooltipType={"none"}
              {...ANIMATION}
            />
            {projection && (
              <Area
                type={"linear"}
                dataKey={"at_risk"}
                className={"results-at-risk"}
                fill={AT_RISK_FILL}
                fillOpacity={0.22}
                stroke={"none"}
                activeDot={false}
                tooltipType={"none"}
                {...PROJECTION_ANIMATION}
              />
            )}
            {projection && (
              <Line
                type={"linear"}
                dataKey={"flat_sql"}
                className={"results-flat"}
                stroke={FLAT_STROKE}
                strokeWidth={2}
                strokeDasharray={"2 5"}
                dot={false}
                activeDot={false}
                tooltipType={"none"}
                {...PROJECTION_ANIMATION}
              />
            )}
            <Line
              type={"monotone"}
              dataKey={"cumulative_mql"}
              stroke={MQL_COLOR}
              strokeWidth={2.5}
              dot={MQL_DOT}
              activeDot={{r: 4}}
              {...ANIMATION}
            />
            <Line
              type={"monotone"}
              dataKey={"cumulative_sql"}
              stroke={SQL_COLOR}
              strokeWidth={3}
              dot={SQL_DOT}
              activeDot={{r: 4.5}}
              {...ANIMATION}
            />
            {projection && (
              <Line
                type={"linear"}
                dataKey={"projected_sql"}
                className={"results-projection"}
                stroke={SQL_COLOR}
                strokeWidth={3}
                strokeDasharray={"7 5"}
                dot={PROJECTED_SQL_DOT}
                activeDot={{r: 4.5}}
                {...PROJECTION_ANIMATION}
              />
            )}
            {projection && (
              <Line
                type={"linear"}
                dataKey={"projected_mql"}
                className={"results-projection"}
                stroke={MQL_COLOR}
                strokeWidth={2.5}
                strokeDasharray={"7 5"}
                dot={PROJECTED_MQL_DOT}
                activeDot={{r: 4}}
                {...PROJECTION_ANIMATION}
              />
            )}
            {projection && (
              <ReferenceLine x={today.date} className={"results-today"} stroke={TODAY_STROKE} strokeDasharray={"4 4"} />
            )}
            <EndPills pills={pills} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SqlMqlLegend>
        {projection && (
          <>
            <LegendItem
              label={t("ReachEngine_ResultsOverTime_LegendStopsToday")}
              marker={<span aria-hidden className={"w-[14px] border-t-[3px] border-dotted border-cr-slate-40"} />}
            />
            <LegendItem
              label={t("ReachEngine_ResultsOverTime_LegendAtRisk")}
              marker={<span aria-hidden className={"h-[10px] w-[12px] rounded-[2px] bg-[#11DDDD] opacity-40"} />}
            />
          </>
        )}
      </SqlMqlLegend>
      {/* A fraction of a result over the horizon is not worth saying. */}
      {projection && (projection.lostSql > 0 || projection.lostMql > 0) && (
        <p
          data-testid={"results-callout"}
          className={"mt-[12px] border-t border-cr-slate-2 pt-[12px] text-[12.5px] font-semibold text-cr-blue-dark"}
        >
          <Trans
            i18nKey={"ReachEngine_ResultsOverTime_Callout"}
            values={{sql: projection.lostSql.toLocaleString(), mql: projection.lostMql.toLocaleString()}}
            components={{number: <b className={"font-bold text-cr-blue"} />}}
          />
        </p>
      )}
    </>
  )
}

const PathStep: React.FC<{value: number; label: string; first?: boolean}> = ({value, label, first}) => (
  <li className={"flex items-baseline gap-[10px]"}>
    {!first && (
      <span aria-hidden className={"font-bold text-cr-slate-30"}>
        →
      </span>
    )}
    <span className={"flex items-baseline gap-[6px]"}>
      <span className={"text-[15px] font-bold text-cr-slate-90 tabular-nums"}>{value.toLocaleString()}</span>
      <span className={"text-cr-slate-60"}>{label}</span>
    </span>
  </li>
)

export const PathStrip: React.FC<{funnel: TReachMeta["funnel"]}> = ({funnel}) => {
  const {t} = useTranslation()

  return (
    <div
      data-testid={"path-strip"}
      className={"mt-[16px] flex flex-wrap items-center gap-[10px] border-t border-cr-slate-2 pt-[14px] text-[12px]"}
    >
      <ol role={"list"} className={"flex flex-wrap items-baseline gap-[10px]"}>
        <PathStep first value={funnel.leads_in_flows} label={t("ReachEngine_PathStrip_ActiveInFlows")} />
        <PathStep value={funnel.responses} label={t("ReachEngine_PathStrip_Responses", {count: funnel.responses})} />
        <PathStep value={funnel.mql} label={t("ReachEngine_Metric_Mql")} />
        <PathStep value={funnel.sql} label={t("ReachEngine_Metric_Sql")} />
      </ol>
      <p
        className={
          "rounded-[8px] bg-cr-blue-pale px-[12px] py-[6px] text-[11.5px] font-semibold text-cr-blue-dark sm:ml-auto"
        }
      >
        <Trans
          i18nKey={"ReachEngine_PathStrip_BackInPool"}
          values={{value: funnel.back_in_pool.toLocaleString()}}
          components={{number: <span className={"font-bold text-cr-blue tabular-nums"} />}}
        />
      </p>
    </div>
  )
}

type TResultsOverTimeCardProps = {
  range: TReachRange
  period: TReachPeriod
  funnel: TReachMeta["funnel"]
  prospects: TReachMeta["flow_types"]["leads"]
}

export const ResultsOverTimeCard: React.FC<TResultsOverTimeCardProps> = ({range, period, funnel, prospects}) => {
  const {t} = useTranslation()
  const {data: buckets, error, isLoading, refetch} = useReachResultsOverTimeQuery(range)

  const timeline = React.useMemo(() => (buckets ? toTimeline(buckets, new Date()) : []), [buckets])
  const projection = React.useMemo(
    () => projectResults(timeline, windowDaysFor(period, timeline), prospects),
    [timeline, period, prospects]
  )
  const showsChart = !isLoading && !error && buckets && !isEmptyResults(buckets)

  const subtitle =
    showsChart && projection
      ? t("ReachEngine_ResultsOverTime_SubtitleProjected", {
          window:
            period === "30d"
              ? t("ReachEngine_ResultsOverTime_Window30d")
              : t("ReachEngine_ResultsOverTime_WindowLifetime"),
        })
      : t("ReachEngine_ResultsOverTime_Subtitle")

  return (
    <DashboardCard title={t("ReachEngine_ResultsOverTime_Title")} subtitle={subtitle}>
      <div className={"mt-[12px]"}>
        {isLoading ? (
          <div className={"h-60"}>
            <Loading size={"xl"} />
          </div>
        ) : error || !buckets ? (
          <GenericErrorAlert retry={refetch} />
        ) : isEmptyResults(buckets) ? (
          <EmptyState className={"h-60"} />
        ) : (
          <ResultsOverTimeChart timeline={timeline} projection={projection} />
        )}
      </div>
      <PathStrip funnel={funnel} />
    </DashboardCard>
  )
}
